'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useTranslation } from '@/hooks/useTranslation';
import { useProfile } from '@/contexts/ProfileContext';
import { tn, getCustomLabel } from '@/lib/notificationTranslation';

interface HeaderProps {
  onMenuToggle?: () => void;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
  data?: Record<string, unknown>;
}

interface OtherParticipant {
  id: string;
  email: string;
  freelancerProfile?: { firstName: string; lastName: string; profilePhoto?: string } | null;
  recruiterProfile?: { companyName: string; logoUrl?: string } | null;
}

interface Conversation {
  id: string;
  unreadCount: number;
  lastMessageAt?: string | null;
  lastMessage?: { content: string; createdAt: string; senderId: string } | null;
  otherParticipant: OtherParticipant;
  job?: { id: string; title: string } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `${mins}m atrás`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h atrás`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d atrás`;
  return new Date(dateStr).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
}

function getParticipantName(p: OtherParticipant): string {
  if (p.freelancerProfile) return `${p.freelancerProfile.firstName} ${p.freelancerProfile.lastName}`;
  if (p.recruiterProfile) return p.recruiterProfile.companyName;
  return p.email;
}

function getParticipantPhoto(p: OtherParticipant): string | undefined {
  return p.freelancerProfile?.profilePhoto ?? p.recruiterProfile?.logoUrl;
}

function getParticipantInitials(p: OtherParticipant): string {
  const name = getParticipantName(p);
  const parts = name.split(' ');
  return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
}

function notifIcon(type: string): { bg: string; icon: React.ReactNode } {
  const iconClass = 'w-4 h-4';
  switch (type) {
    case 'APPLICATION_SUBMITTED':
    case 'APPLICATION_STATUS_CHANGED':
    case 'JOB_APPROVED':
    case 'JOB_REJECTED':
    case 'ADMIN_SELECT_CANDIDATE_REQUEST':
      return {
        bg: 'bg-teal-100 text-teal-600',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>,
      };
    case 'NEW_MESSAGE':
      return {
        bg: 'bg-blue-100 text-blue-600',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>,
      };
    case 'BID_CREATED':
    case 'BID_APPLICATION_RECEIVED':
    case 'BID_ACCEPTED':
    case 'BID_REJECTED':
    case 'BID_WINNER_SELECTED':
    case 'BID_PAYMENT_RECEIVED':
    case 'BID_DISPUTE_RAISED':
      return {
        bg: 'bg-purple-100 text-purple-600',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      };
    case 'PAYMENT_INITIATED':
    case 'PAYMENT_CONFIRMED':
    case 'PAYMENT_FAILED':
    case 'PAYMENT_PENDING_AFTER_COMPLETION':
    case 'PAYMENT_DISBURSED':
    case 'BANK_TRANSFER_INSTRUCTIONS':
    case 'BANK_TRANSFER_VERIFIED':
      return {
        bg: 'bg-emerald-100 text-emerald-600',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
      };
    case 'PROFILE_VISIBILITY_ACTIVATED':
    case 'PROFILE_VISIBILITY_EXPIRING':
    case 'PROFILE_VISIBILITY_EXPIRED':
    case 'MEMBERSHIP_EXPIRING':
    case 'MEMBERSHIP_EXPIRED':
      return {
        bg: 'bg-amber-100 text-amber-600',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
      };
    case 'INTERVIEW_SCHEDULED':
      return {
        bg: 'bg-indigo-100 text-indigo-600',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
      };
    default:
      return {
        bg: 'bg-gray-100 text-gray-500',
        icon: <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      };
  }
}

/** Map a notification to the deepest meaningful URL. Falls back to /notifications. */
function getNotifUrl(n: Notification): string {
  const d = n.data ?? {};
  switch (n.type) {

    // ── Application events ────────────────────────────────────────────────────
    case 'APPLICATION_SUBMITTED':
      // Recruiter receives this - link to the specific application
      if (d.applicationId) return `/applications/${d.applicationId}`;
      if (d.jobId) return `/jobs/${d.jobId}`;
      return '/applications';

    case 'APPLICATION_STATUS_CHANGED':
      // Freelancer receives this - link to their application
      if (d.applicationId) return `/applications/${d.applicationId}`;
      return '/applications';

    // ── Bids ──────────────────────────────────────────────────────────────────
    case 'BID_CREATED':
    case 'BID_APPLICATION_RECEIVED':
      if (d.applicationId) return `/applications/${d.applicationId}`;
      if (d.jobId) return `/jobs/${d.jobId}`;
      return '/applications';

    case 'BID_ACCEPTED':
    case 'BID_REJECTED':
    case 'BID_WINNER_SELECTED':
    case 'BID_DISPUTE_RAISED':
      if (d.applicationId) return `/applications/${d.applicationId}`;
      return '/applications';

    case 'BID_PAYMENT_RECEIVED':
      if (d.hiringPaymentId) return `/orders/${d.hiringPaymentId}`;
      if (d.applicationId) return `/applications/${d.applicationId}`;
      return '/orders/hiring';

    // ── Jobs ──────────────────────────────────────────────────────────────────
    case 'JOB_APPROVED':
    case 'JOB_REJECTED':
    case 'ADMIN_SELECT_CANDIDATE_REQUEST':
      if (d.jobId) return `/jobs/${d.jobId}`;
      return '/jobs';

    // ── Interviews ────────────────────────────────────────────────────────────
    case 'INTERVIEW_SCHEDULED':
      if (d.interviewId) return `/interviews/${d.interviewId}`;
      return '/interviews';

    // ── Hiring payments ───────────────────────────────────────────────────────
    // Always prefer the specific order detail page over the application page
    case 'PAYMENT_INITIATED':
    case 'PAYMENT_CONFIRMED':
    case 'PAYMENT_FAILED':
    case 'PAYMENT_PENDING_AFTER_COMPLETION':
    case 'PAYMENT_DISBURSED':
      if (d.hiringPaymentId) return `/orders/${d.hiringPaymentId}`;
      if (d.membershipOrderId ?? d.orderId) return `/orders/${d.membershipOrderId ?? d.orderId}`;
      if (d.applicationId) return `/applications/${d.applicationId}`;
      return '/orders/hiring';

    case 'BANK_TRANSFER_INSTRUCTIONS':
    case 'BANK_TRANSFER_VERIFIED':
      // Could be a hiring payment OR a membership order bank transfer
      if (d.hiringPaymentId) return `/orders/${d.hiringPaymentId}`;
      if (d.membershipOrderId ?? d.orderId) return `/orders/${d.membershipOrderId ?? d.orderId}`;
      if (d.applicationId) return `/applications/${d.applicationId}`;
      return '/orders/hiring';

    // ── Profile / membership ──────────────────────────────────────────────────
    case 'PROFILE_VISIBILITY_ACTIVATED':
    case 'PROFILE_VISIBILITY_EXPIRING':
    case 'PROFILE_VISIBILITY_EXPIRED':
      // Link to the specific membership order if we have it, else to the membership page
      if (d.membershipOrderId ?? d.orderId) return `/orders/${d.membershipOrderId ?? d.orderId}`;
      return '/membership';

    case 'MEMBERSHIP_EXPIRING':
    case 'MEMBERSHIP_EXPIRED':
      if (d.membershipOrderId ?? d.orderId) return `/orders/${d.membershipOrderId ?? d.orderId}`;
      return '/membership';

    case 'RECRUITMENT_PLAN_EXPIRING_15D':
    case 'RECRUITMENT_PLAN_EXPIRING_7D':
    case 'RECRUITMENT_PLAN_EXPIRING_3D':
    case 'RECRUITMENT_PLAN_EXPIRED':
    case 'PROFILE_PROMOTION_EXPIRING_15D':
    case 'PROFILE_PROMOTION_EXPIRING_7D':
    case 'PROFILE_PROMOTION_EXPIRING_3D':
    case 'PROFILE_PROMOTION_EXPIRED':
      if (d.membershipOrderId ?? d.orderId) return `/orders/${d.membershipOrderId ?? d.orderId}`;
      return '/membership';

    case 'VERIFICATION_BADGE_GRANTED':
    case 'VERIFICATION_BADGE_REVOKED':
      return '/profile';

    // ── Support ───────────────────────────────────────────────────────────────
    case 'SUPPORT_TICKET_REPLY':
      return '/support';

    // ── Broadcasts / general ─────────────────────────────────────────────────
    case 'ADMIN_BROADCAST':
    case 'JOB_MATCH_FOUND':
      if (d.jobId) return `/jobs/${d.jobId}`;
      return '/notifications';

    default:
      return '/notifications';
  }
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Header({ onMenuToggle }: HeaderProps) {
  const { data: session, status } = useSession();
  const isLoggedIn = status === 'authenticated';
  const { avatarUrl, displayName: ctxDisplayName } = useProfile();
  const user = session?.user ? {
    name: ctxDisplayName ?? session.user.name ?? session.user.email ?? 'Utilizador',
    email: session.user.email ?? '',
  } : null;
  const router = useRouter();
  const pathname = usePathname();
  const { t, lang, translations } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Live data
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [msgsLoading, setMsgsLoading] = useState(false);

  // Refs for click-outside handling
  const notifRef = useRef<HTMLDivElement>(null);
  const msgRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // Close menu when navigating
  useEffect(() => { setIsMobileMenuOpen(false); }, [pathname]);

  // Click-outside to close dropdowns
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setIsNotificationsOpen(false);
      if (msgRef.current && !msgRef.current.contains(e.target as Node)) setIsMessagesOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch unread counts on mount (light poll every 60s)
  const fetchCounts = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const r = await fetch('/api/v1/me/counts');
      const j = await r.json();
      if (j.success) {
        setUnreadNotifs(j.data.unreadNotifications ?? 0);
        setUnreadMessages(j.data.unreadMessages ?? 0);
      }
    } catch { /* silent */ }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Load notifications when panel opens
  const openNotifications = async () => {
    if (isNotificationsOpen) { setIsNotificationsOpen(false); return; }
    setIsMessagesOpen(false);
    setIsDropdownOpen(false);
    setIsNotificationsOpen(true);
    if (notifications.length > 0) return; // already loaded
    setNotifsLoading(true);
    try {
      const r = await fetch('/api/v1/notifications?limit=8');
      const j = await r.json();
      if (j.success) {
        setNotifications(j.data.notifications ?? []);
        setUnreadNotifs(j.data.unreadCount ?? 0);
      }
    } catch { /* silent */ }
    finally { setNotifsLoading(false); }
  };

  // Load conversations when panel opens
  const openMessages = async () => {
    if (isMessagesOpen) { setIsMessagesOpen(false); return; }
    setIsNotificationsOpen(false);
    setIsDropdownOpen(false);
    setIsMessagesOpen(true);
    if (conversations.length > 0) return;
    setMsgsLoading(true);
    try {
      const r = await fetch('/api/v1/conversations?limit=6');
      const j = await r.json();
      if (j.success) {
        const convos: Conversation[] = j.data ?? [];
        setConversations(convos);
        setUnreadMessages(convos.reduce((sum, c) => sum + c.unreadCount, 0));
      }
    } catch { /* silent */ }
    finally { setMsgsLoading(false); }
  };

  // Mark all notifications as read
  const markAllRead = async () => {
    try {
      await fetch('/api/v1/notifications', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      setNotifications(n => n.map(x => ({ ...x, isRead: true })));
      setUnreadNotifs(0);
    } catch { /* silent */ }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (onMenuToggle) onMenuToggle();
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-8 h-16 md:h-22 flex items-center justify-between gap-4">

        {/* Left: Menu + Logo */}
        <div className="flex items-center gap-4">
          <button className="lg:hidden text-gray-600 hover:text-gray-900 focus:outline-none" onClick={toggleMobileMenu} aria-label="Toggle menu">
            {isMobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            )}
          </button>
          <Link href="/" className="flex-shrink-0">
            <img src="/logo.png" alt="Autonomo" className="h-7 md:h-10 w-auto mix-blend-multiply" />
          </Link>
        </div>

        {/* Center: Nav + Search */}
        <div className="hidden lg:flex flex-1 items-center max-w-3xl gap-4">
          <div className="relative group">
            <button className="flex items-center gap-1 text-gray-700 font-medium hover:text-teal-600 transition py-2">
              <span suppressHydrationWarning>{t('header.explore')}</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <Link href="/jobs" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-600 font-medium">{t('header.jobs')}</Link>
              <Link href="/portfolios" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-600 font-medium">{t('header.freelancers')}</Link>
              <Link href="/services" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-600 font-medium">{t('header.services')}</Link>
              <div className="border-t border-gray-100 my-1" />
              <Link href="/request-hiring" className="block px-4 py-2 text-sm text-teal-700 hover:bg-teal-50 font-semibold">{t('header.requestHiring')}</Link>
            </div>
          </div>

          <div className="flex-1 flex items-center px-4 py-2 bg-gray-100 rounded-full border border-transparent focus-within:border-gray-300 focus-within:bg-white transition-colors">
            <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input type="text" className="bg-transparent border-none outline-none w-full text-sm text-gray-900 placeholder-gray-500"
              placeholder={t('header.searchPlaceholder')} value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); router.push(`/jobs?q=${encodeURIComponent(searchQuery)}`); } }} />
            <button type="button" onClick={() => router.push(`/jobs?q=${encodeURIComponent(searchQuery)}`)}
              className="bg-teal-500 text-white px-6 py-1.5 -mr-2 ml-2 rounded-full text-sm font-medium hover:bg-teal-600 transition">
              {t('header.search')}
            </button>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-3">
            <LanguageSwitcher />
            {!isLoggedIn && <Link href="/login" className="text-gray-600 hover:text-gray-900 font-medium text-sm px-3 py-2">{t('header.logIn')}</Link>}
            {!isLoggedIn && <Link href="/register" className="text-teal-600 border border-teal-600 px-4 py-2 rounded-full font-medium text-sm hover:bg-teal-50 transition whitespace-nowrap">{t('header.signUp')}</Link>}
            {isLoggedIn && (
              <Link href="/jobs/new" className="bg-teal-500 text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-teal-600 transition whitespace-nowrap">{t('header.postJob')}</Link>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isLoggedIn && (
              <div className="hidden sm:flex items-center gap-1">

                {/* ── Notifications ── */}
                <div ref={notifRef} className="relative">
                  <button
                    id="notif-btn"
                    onClick={openNotifications}
                    className={`relative p-2 rounded-xl transition ${isNotificationsOpen ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
                    aria-label="Notificações"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadNotifs > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                        {unreadNotifs > 99 ? '99+' : unreadNotifs}
                      </span>
                    )}
                  </button>

                  {isNotificationsOpen && (
                    <div className="absolute top-full right-0 mt-2 w-84 min-w-[340px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900 text-sm">{t('header.notificationsTitle')}</h3>
                          {unreadNotifs > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadNotifs}</span>}
                        </div>
                        {unreadNotifs > 0 && (
                          <button onClick={markAllRead} className="text-xs text-teal-600 font-semibold hover:underline">
                            {t('header.markAllRead')}
                          </button>
                        )}
                      </div>

                      {/* List */}
                      <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-50">
                        {notifsLoading ? (
                          Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="p-4 flex gap-3 animate-pulse">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex-shrink-0" />
                              <div className="flex-1 space-y-2"><div className="h-3 bg-gray-100 rounded w-3/4" /><div className="h-2.5 bg-gray-100 rounded w-full" /></div>
                            </div>
                          ))
                        ) : notifications.length === 0 ? (
                          <div className="py-12 text-center">
                            <div className="w-12 h-12 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                            </div>
                            <p className="text-sm text-gray-500 font-medium">{t('header.noNotifications')}</p>
                          </div>
                        ) : (
                          notifications.map(n => {
                            const { bg, icon } = notifIcon(n.type);
                            const url = getNotifUrl(n);
                            const customLabel = getCustomLabel(translations);
                            const { title: notifTitle, body: notifBody, isCustomBody } = tn(
                              { title: n.title, body: n.body, data: n.data as Record<string, unknown> | null },
                              lang,
                              translations,
                              customLabel,
                            );
                            return (
                              <button
                                key={n.id}
                                className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-gray-50 transition cursor-pointer ${!n.isRead ? 'bg-blue-50/40' : ''}`}
                                onClick={() => {
                                  setNotifications(ns => ns.map(x => x.id === n.id ? { ...x, isRead: true } : x));
                                  if (!n.isRead) setUnreadNotifs(c => Math.max(0, c - 1));
                                  fetch('/api/v1/notifications', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: [n.id] }) }).catch(() => { });
                                  setIsNotificationsOpen(false);
                                  router.push(url);
                                }}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${bg}`}>{icon}</div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{notifTitle}</p>
                                  {isCustomBody && (
                                    <p className="text-[10px] text-teal-600 font-medium mb-0.5">{customLabel}</p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notifBody}</p>
                                  <span className="text-[10px] text-gray-400 mt-1 block">{relativeTime(n.createdAt)}</span>
                                </div>
                                {!n.isRead && <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />}
                              </button>
                            );
                          })
                        )}
                      </div>

                      {/* Footer */}
                      <div className="border-t border-gray-100">
                        <Link href="/notifications" onClick={() => setIsNotificationsOpen(false)}
                          className="block text-center py-3 text-sm font-semibold text-teal-600 hover:bg-gray-50 transition">
                          {t('header.viewAllNotifications')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Messages ── */}
                <div ref={msgRef} className="relative">
                  <button
                    id="messages-btn"
                    onClick={openMessages}
                    className={`relative p-2 rounded-xl transition ${isMessagesOpen ? 'text-teal-600 bg-teal-50' : 'text-gray-400 hover:text-teal-600 hover:bg-teal-50'}`}
                    aria-label="Mensagens"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    {unreadMessages > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                  </button>

                  {isMessagesOpen && (
                    <div className="absolute top-full right-0 mt-2 w-84 min-w-[340px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900 text-sm">{t('header.messagesTitle')}</h3>
                          {unreadMessages > 0 && <span className="bg-teal-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadMessages}</span>}
                        </div>
                        <Link href="/messages" onClick={() => setIsMessagesOpen(false)} className="text-xs text-teal-600 font-semibold hover:underline">
                          {t('header.newMessage')}
                        </Link>
                      </div>

                      {/* List */}
                      <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-50">
                        {msgsLoading ? (
                          Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="p-4 flex gap-3 animate-pulse">
                              <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0" />
                              <div className="flex-1 space-y-2"><div className="h-3 bg-gray-100 rounded w-1/2" /><div className="h-2.5 bg-gray-100 rounded w-3/4" /></div>
                            </div>
                          ))
                        ) : conversations.length === 0 ? (
                          <div className="py-12 text-center">
                            <div className="text-3xl mb-2"></div>
                            <p className="text-sm text-gray-500 font-medium">{t('header.noConversations')}</p>
                            <p className="text-xs text-gray-400 mt-1">{t('header.noConversationsHint')}</p>
                          </div>
                        ) : (
                          conversations.map(c => {
                            const name = getParticipantName(c.otherParticipant);
                            const photo = getParticipantPhoto(c.otherParticipant);
                            const initials = getParticipantInitials(c.otherParticipant);
                            const hasUnread = c.unreadCount > 0;
                            return (
                              <Link key={c.id}
                                href={`/messages?conversation=${c.id}`}
                                onClick={() => { setIsMessagesOpen(false); setUnreadMessages(m => Math.max(0, m - c.unreadCount)); setConversations(cs => cs.map(x => x.id === c.id ? { ...x, unreadCount: 0 } : x)); }}
                                className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition ${hasUnread ? 'bg-teal-50/30' : ''}`}
                              >
                                {/* Avatar */}
                                <div className="relative flex-shrink-0">
                                  {photo ? (
                                    <img src={photo} alt={name} className="w-10 h-10 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-sm font-black text-white">
                                      {initials}
                                    </div>
                                  )}
                                  {hasUnread && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-teal-500 rounded-full border-2 border-white" />}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{name}</span>
                                    {c.lastMessageAt && <span className={`text-[10px] flex-shrink-0 ${hasUnread ? 'text-teal-600 font-semibold' : 'text-gray-400'}`}>{relativeTime(c.lastMessageAt)}</span>}
                                  </div>
                                  {c.job && <div className="text-[10px] text-teal-500 font-medium truncate">{c.job.title}</div>}
                                  {c.lastMessage && (
                                    <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>
                                      {c.lastMessage.content}
                                    </p>
                                  )}
                                </div>

                                {hasUnread && (
                                  <span className="min-w-[20px] h-5 bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 flex-shrink-0">
                                    {c.unreadCount}
                                  </span>
                                )}
                              </Link>
                            );
                          })
                        )}
                      </div>

                      {/* Footer */}
                      <div className="border-t border-gray-100">
                        <Link href="/messages" onClick={() => setIsMessagesOpen(false)}
                          className="block text-center py-3 text-sm font-semibold text-teal-600 hover:bg-gray-50 transition">
                          {t('header.openInbox')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* ── Profile Dropdown ── */}
            {isLoggedIn && user && (
              <div ref={profileRef} className="relative">
                <button onClick={() => { setIsDropdownOpen(!isDropdownOpen); setIsNotificationsOpen(false); setIsMessagesOpen(false); }}
                  className="flex items-center gap-2 focus:outline-none p-1 rounded-xl hover:bg-gray-100 transition">
                  <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs overflow-hidden flex-shrink-0">
                    {avatarUrl ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      user.name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U'
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-gray-700">{(user.name || 'Utilizador').split(' ')[0]}</div>
                  </div>
                  <svg className={`hidden md:block w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                    <div className="px-4 py-2.5 border-b border-gray-50">
                      <div className="text-sm font-bold text-gray-900 truncate">{user.name}</div>
                      <div className="text-xs text-gray-400 truncate">{user.email}</div>
                    </div>
                    <Link href="/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700" onClick={() => setIsDropdownOpen(false)}>
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="3" width="7" height="7" rx="1" strokeWidth={2} /><rect x="14" y="3" width="7" height="7" rx="1" strokeWidth={2} /><rect x="3" y="14" width="7" height="7" rx="1" strokeWidth={2} /><rect x="14" y="14" width="7" height="7" rx="1" strokeWidth={2} /></svg>
                      {t('header.dashboard')}
                    </Link>
                    <Link href="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700" onClick={() => setIsDropdownOpen(false)}>
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      {t('header.myProfile')}
                    </Link>
                    <Link href="/settings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700" onClick={() => setIsDropdownOpen(false)}>
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><circle cx="12" cy="12" r="3" strokeWidth={2} /></svg>
                      {t('header.settings')}
                    </Link>
                    <div className="border-t border-gray-100 mt-1">
                      <button onClick={() => { signOut({ callbackUrl: '/' }); setIsDropdownOpen(false); }}
                        className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        {t('header.logOut')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-4 shadow-lg absolute w-full left-0 z-40">
          <div className="flex bg-gray-100 rounded-full focus-within:border-gray-300 focus-within:bg-white transition-colors border border-transparent p-1">
            <div className="flex items-center flex-1 px-3">
              <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input type="text" className="bg-transparent border-none outline-none w-full text-sm text-gray-900" placeholder={t('header.searchPlaceholder')}
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setIsMobileMenuOpen(false); router.push(`/jobs?q=${encodeURIComponent(searchQuery)}`); } }} />
            </div>
            <button type="button" onClick={() => { setIsMobileMenuOpen(false); router.push(`/jobs?q=${encodeURIComponent(searchQuery)}`); }}
              className="bg-teal-500 text-white px-4 py-1.5 rounded-full text-xs font-medium">{t('header.search')}</button>
          </div>

          <nav className="flex flex-col gap-1">
            <Link href="/jobs" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">{t('header.jobs')}</Link>
            <Link href="/portfolios" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">{t('header.freelancers')}</Link>
            <Link href="/services" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">{t('header.services')}</Link>
            <Link href="/request-hiring" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-teal-700 hover:bg-teal-50 rounded-lg text-sm font-semibold">{t('header.requestHiring')}</Link>
          </nav>

          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs text-gray-500 font-semibold tracking-wider uppercase">{t('header.language')}</span>
              <LanguageSwitcher />
            </div>
          </div>

          {isLoggedIn && user ? (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center gap-3 px-4 mb-3">
                <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">{user.name.split(' ')[0]}</div>
                  <div className="text-xs text-gray-400">{user.email}</div>
                </div>
                {/* Mobile unread badges */}
                {(unreadNotifs > 0 || unreadMessages > 0) && (
                  <div className="ml-auto flex gap-2">
                    {unreadNotifs > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadNotifs} notif</span>}
                    {unreadMessages > 0 && <span className="bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadMessages} msg</span>}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 px-1">
                <Link href="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">{t('header.dashboard')}</Link>
                <Link href="/profile" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">{t('sidebar.items.manageProfile')}</Link>
                <Link href="/messages" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium flex items-center justify-between">
                  {t('header.messages')} {unreadMessages > 0 && <span className="bg-teal-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadMessages}</span>}
                </Link>
                <Link href="/notifications" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium flex items-center justify-between">
                  {t('header.notifications')} {unreadNotifs > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadNotifs}</span>}
                </Link>
                <Link href="/settings" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium">{t('header.settings')}</Link>
                <button onClick={() => { signOut({ callbackUrl: '/' }); setIsMobileMenuOpen(false); }} className="text-left block px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">{t('header.logOut')}</button>
              </div>
            </div>
          ) : (
            <div className="pt-3 border-t border-gray-100 flex gap-2">
              <Link href="/login" onClick={() => setIsMobileMenuOpen(false)} className="flex-1 border text-center border-teal-600 text-teal-600 py-2 rounded-full font-medium text-sm">{t('header.logIn')}</Link>
              <Link href="/register" onClick={() => setIsMobileMenuOpen(false)} className="flex-1 bg-teal-600 text-center text-white py-2 rounded-full font-medium text-sm">{t('header.signUp')}</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
